import { AggregateElementViewModel } from './aggregate-element-view-model';
import { MetaDataUtils } from '../meta-data';
import { ColumnsUtils } from './columns_utils';
import { ColumnInfoCollection } from './column-info-collection';
import { AggregateMetaDataInterface, CollectionViewModelInterface, ColumnInfoCollectionInterface, ColumnInfoInterface, ColumnSettingsInterface, ColumnsProviderInterface, CoreOrchestratorViewModelInterface, GetGridColumnsParamsInterface, IdentityInterface, InternalCollectionMetaDataInterface, ModelInterface, RootViewModelInterface } from '@nts/std/interfaces';
import { LogService } from '@nts/std/utility';
import { ReplaySubject } from 'rxjs';
import { ClassConstructor } from '@nts/std/serialization';
import { PanelViewModel } from './panel.view-model';
import { AccessMode, ClassInformationType, ExternalFieldInputMode } from '@nts/std/types';
import { ClassInformationInterface } from '@nts/std/interfaces';
import { ColumnInfo } from './column-info';

export type GetGridColumnsParams = GetGridColumnsParamsInterface | string;

export class RootViewModel<TModel extends ModelInterface<TIdentity>, TIdentity extends IdentityInterface>
    extends AggregateElementViewModel<TModel, TIdentity> implements RootViewModelInterface, ClassInformationInterface {

    classType = ClassInformationType.RootViewModel;
    currentStateChanged: ReplaySubject<void> = new ReplaySubject();
    panels: Map<string, PanelViewModel> = new Map();

    private _columnsProvider: ColumnsProviderInterface;

    //#region getGridColumns
    /**
     * @param {GetGridColumnsParamsInterface} params        {@link GetGridColumnsParamsInterface}
     * @returns                                             La lista delle colonne costruite in base ai paramtri passati.
     */
    getGridColumns(
        params: GetGridColumnsParamsInterface
    ): ColumnInfoCollectionInterface;
    /**
     * @deprecated Dalla versione REL_8.0.x escluso utilizzare "Named Parameters" seguendo l'interfaccia GetGridColumnsParams.
     *
     * @param {string} fullPathName                         Percorso relativo alla root del modello della collection.
     *
     * @param {string} entityTypeName                       Modello della collection.
     *
     * @param {string[]} visibilityDefinition               Accetta la lista delle property da rendere visibili.
     *                                                      Le property di collegamento con il parent saranno sempre invisibili.
     *                                                      Le property serial saranno sempre invisibili.
     *                                                      Default tutto invisibile.
     *
     * @param {boolean | string[]} [editableDefinition]     Lista delle property da disabilitare, in alternativa se viene passato false, tutto viene disabilitato.
     *                                                      Le property di collegamento con il parent saranno sempre disabilitate.
     *                                                      Le property serial saranno sempre disabilitate.
     *                                                      Le property row number saranno sempre disabilitate.
     *                                                      Le ulteriori property identity possono essere abilitate solo se la riga è nuova.
     *                                                      Le decode degli external saranno sempre disabilitate.
     *                                                      Default tutto abilitato tranne le eccezzioni definite precedentemente.
     *
     * @param {string[]} [orderDefinition]                  Imposta lo stesso ordinamento delle property passate.
     *                                                      Le property non elencate avranno tutte la stessa position superiore all'ultima dichiarata.
     *                                                      Tutte le property che saranno sempre invisibili avrannno position 0.
     *                                                      Default stesso ordinamento delle visibilityDefinition altrimenti ordine casuale in base alla lettura dei meta dati.
     *
     * @param widthDefinition                               Definisce le dimensioni delle colonne
     *
     * @param autocompleteDefinition                        Definisce la definizion custom per gli autocomplete
     *
     * @param customColumns                                 Eventuali colonne custom da aggiungere alla griglia
     *
     * @returns                                             La lista delle colonne costruite in base ai paramtri passati.
     */
    getGridColumns(
        fullPathName: string,
        entityTypeName?: string,
        visibilityDefinition?: string[],
        editableDefinition?: string[] | boolean,
        orderDefinition?: string[],
        widthDefinition?: { fullPropertyName: string, isAutoSize?: boolean, width: number; }[],
        autocompleteDefinition?: {

            /**
             * Percorso completo della code property con incluso il suo nome separata da '.'
             */
            fullPropertyName: string,

            /**
             * Default ExternalFieldInputMode.Autocomplete
             */
            inputMode?: ExternalFieldInputMode,

            /**
             * @deprecated Utilizzare il campo decodeProperties e aggiungere i codici che si vogliono visualizzare
             */
            showCodeInDescription?: boolean,

            /**
             * Properties che vogliamo visualizzare
             */
            decodeProperties?: string[],

            /**
             * Properties che vogliamo utilizzare per la ricerca
             */
            searchProperties?: string[],

            /**
             * Simbolo di separazione tra piu properties
             */
            fieldSeparator?: string;
        }[],
        columns?: ColumnSettingsInterface[],
        customColumns?: ColumnInfoInterface[]
    ): ColumnInfoCollectionInterface;
    /**
     * Restituisce le colonne per la griglia
     */
    getGridColumns(
        fullPathNameOrOpts: GetGridColumnsParams,
        entityTypeName: string = "",
        visibilityDefinition: string[] = null,
        editableDefinition: string[] | boolean = null,
        orderDefinition: string[] = null,
        widthDefinition: { fullPropertyName: string, isAutoSize?: boolean, width: number; }[] = null,
        autocompleteDefinition: {
            fullPropertyName: string,
            inputMode?: ExternalFieldInputMode,
            showCodeInDescription?: boolean,
            decodeProperties?: string[],
            searchProperties?: string[],
            fieldSeparator?: string;
        }[] = null,
        columns: ColumnSettingsInterface[] = undefined,
        customColumns: ColumnInfoInterface[] = null
    ): ColumnInfoCollectionInterface {

        // Destructuring sicuro all'inizio con type narrowing
        let finalFullPathName: string;
        let finalEntityTypeName = entityTypeName;
        let finalVisibilityDefinition = visibilityDefinition;
        let finalEditableDefinition = editableDefinition;
        let finalOrderDefinition = orderDefinition;
        let finalWidthDefinition = widthDefinition;
        let finalAutocompleteDefinition = autocompleteDefinition;
        let finalColumns = columns;
        let finalCustomColumns = customColumns;

        if (typeof fullPathNameOrOpts !== 'string') {
            const opts = fullPathNameOrOpts;
            finalFullPathName = opts.fullPathName;
            finalEntityTypeName = opts.entityTypeName ?? entityTypeName;
            finalVisibilityDefinition = opts.visibilityDefinition ?? visibilityDefinition;
            finalEditableDefinition = opts.editableDefinition ?? editableDefinition;
            finalOrderDefinition = opts.orderDefinition ?? orderDefinition;
            finalWidthDefinition = opts.widthDefinition ?? widthDefinition;
            finalAutocompleteDefinition = opts.autocompleteDefinition ?? autocompleteDefinition;
            finalColumns = opts.columns ?? columns;
            finalCustomColumns = opts.customColumns ?? customColumns;
        } else {
            finalFullPathName = fullPathNameOrOpts;
        }

        let headers: { fullPathPropertyName: string, header: string, headerTooltip: string; }[] = [];
        let formattedValueDefinition: { fullPropertyName: string, useFormattedValue: boolean; }[] = [];

        if (finalColumns?.length > 0) {

            // imposto i default basati sulle colonne passate
            finalColumns = finalColumns.map((c) => {
                return {
                    ...c,
                    isEnabled: c.isEnabled ?? false,
                    isVisible: c.isVisible ?? true,
                    order: c.order ?? -1,
                    useFormattedValue: c.useFormattedValue ?? true,
                    isCustom: c.isCustom ?? false,
                    isAutoSize: c.isAutoSize ?? true,
                };
            });

            finalVisibilityDefinition = finalColumns.map((c) => {
                return c.isVisible ? c.fullPathPropertyName : null;
            }).filter((c) => c != null);

            finalEditableDefinition = finalColumns.reduce((acc, c) => {
                if (c.isEnabled === false) {
                    acc.push(c.fullPathPropertyName);
                }
                return acc;
            }, [] as string[]);

            finalOrderDefinition = finalColumns.map((c) => c.fullPathPropertyName);

            // se defiisco il campo order dentro le colonne sovrascrivo l'orderDefinition
            if (finalColumns.some((c) => c.order >= 0)) {
                finalOrderDefinition = finalColumns
                    .filter((c) => c.order >= 0)
                    .sort((a, b) => a.order - b.order)
                    .map((c) => c.fullPathPropertyName);
            }

            finalAutocompleteDefinition = finalColumns
                .map((c) => {
                    return {
                        ...c.autocompleteDefinition,
                        fullPropertyName: c.fullPathPropertyName,
                    };
                });

            headers = finalColumns.filter(c => c.header || c.headerTooltip).map((c) => {
                return {
                    fullPathPropertyName: c.fullPathPropertyName,
                    header: c.header,
                    headerTooltip: c.headerTooltip
                };
            });

            finalWidthDefinition = finalColumns
                .map((c) => {
                    return {
                        fullPropertyName: c.fullPathPropertyName,
                        isAutoSize: c.isAutoSize,
                        width: c.width
                    };
                });

            formattedValueDefinition = finalColumns
                .map((c) => {
                    return {
                        fullPropertyName: c.fullPathPropertyName,
                        useFormattedValue: c.useFormattedValue
                    };
                });

            // Gestisce le colonne custom
            finalCustomColumns = finalColumns
                .filter((c) => c.isCustom)
                .map((c) => {

                    const propertyName = c.fullPathPropertyName.includes('.') ?
                        c.fullPathPropertyName.substring(c.fullPathPropertyName.lastIndexOf('.') + 1) :
                        c.fullPathPropertyName;
                    const path = c.fullPathPropertyName.includes('.') ?
                        c.fullPathPropertyName.substring(0, c.fullPathPropertyName.lastIndexOf('.')) :
                        '';

                    const columnInfo = new ColumnInfo(
                        propertyName, path
                    );

                    columnInfo.header = c.header;
                    columnInfo.headerTooltip = c.headerTooltip;
                    return columnInfo;
                });
        }

        finalCustomColumns = finalCustomColumns?.length > 0 ? finalCustomColumns : this._columnsProvider.customGridColumns.get(finalEntityTypeName);

        let gridColumns: ColumnInfoCollection;

        const gridUserLayout = this._columnsProvider.userLayoutMetaData?.grids?.length > 0 ?
            this._columnsProvider.userLayoutMetaData.grids.find((g) => {

                // utilizzo path e name perche' il fullPathName potrebbe essere errato (non ha i selectedItem)
                const fullPathNameCamelCase = ((g.path?.length > 0 ? g.path + '.' : '') + g.name)
                    .split('.')
                    .map((f) => MetaDataUtils.toCamelCase(f))
                    .join('.');

                return fullPathNameCamelCase === finalFullPathName;
            })
            : null;

        // rimuovo le colonne invisibili
        if (gridUserLayout?.customizedColumns?.length > 0) {
            gridUserLayout.customizedColumns = gridUserLayout.customizedColumns.filter((c) => c.isUserVisible);
        }

        if (!gridUserLayout && this._columnsProvider.userLayoutMetaData?.grids?.filter((g) => g.fullPathName != 'MasterDetailGrid')?.length > 0) {
            LogService.log(
                `ATTENZIONE potresti non aver definito correttamente il fullPathName nella definizione di colonna di ${finalEntityTypeName}, path corrente: ${finalFullPathName}`,
                this._columnsProvider.userLayoutMetaData?.grids
            );
        }

        // La grid user layout sarà presente solo nel layout standard
        if (gridUserLayout?.customizedColumns?.length > 0) {
            const gridUserLayoutFullPathNameCamelCase = gridUserLayout.fullPathName
                .split('.')
                .map((f) => MetaDataUtils.toCamelCase(f))
                .join('.');

            finalVisibilityDefinition = gridUserLayout?.customizedColumns.map((c) => {
                let temp = c.fullPathName
                    .split('.')
                    .map((f) => MetaDataUtils.toCamelCase(f))
                    .join('.');

                if (temp.startsWith(gridUserLayoutFullPathNameCamelCase)) {
                    temp = temp.substring(gridUserLayoutFullPathNameCamelCase.length + 1);
                }

                return temp.replace('selectedItem.', '');
            });

            finalWidthDefinition = gridUserLayout?.customizedColumns.map((c) => {
                let temp = c.fullPathName
                    .split('.')
                    .map((f) => MetaDataUtils.toCamelCase(f))
                    .join('.');

                if (temp.startsWith(gridUserLayoutFullPathNameCamelCase)) {
                    temp = temp.substring(gridUserLayoutFullPathNameCamelCase.length + 1);
                }

                const fullPropertyName = temp.replace('selectedItem.', '');
                const isAutoSize = c.isAutoSize;
                const width = c.width;
                return {
                    fullPropertyName, isAutoSize, width
                };
            }) ?? finalWidthDefinition;

            finalVisibilityDefinition = this.filterPropertyListBySecurity(finalFullPathName, finalVisibilityDefinition ?? []);

            gridColumns = ColumnsUtils.getGridColumns(
                this._columnsProvider,
                finalEntityTypeName,
                finalVisibilityDefinition,
                finalEditableDefinition,
                finalVisibilityDefinition,
                finalWidthDefinition,
                finalAutocompleteDefinition,
                finalCustomColumns,
                headers,
                formattedValueDefinition
            );

        } else {

            finalVisibilityDefinition = this.filterPropertyListBySecurity(finalFullPathName, finalVisibilityDefinition ?? []);

            // Colonne definite nel template
            gridColumns = ColumnsUtils.getGridColumns(
                this._columnsProvider,
                finalEntityTypeName,
                finalVisibilityDefinition,
                finalEditableDefinition,
                finalOrderDefinition,
                finalWidthDefinition,
                finalAutocompleteDefinition,
                finalCustomColumns,
                headers,
                formattedValueDefinition
            );
        }

        return gridColumns;
    }

    //#endregion getGridColumns

    private filterPropertyListBySecurity(collectionFullPathName: string, propertyList: string[]) {
        const foundCollectionMetaData = MetaDataUtils.getAssociationMetaDataDataFromPath(this.aggregateMetaData.rootMetaData, collectionFullPathName) as InternalCollectionMetaDataInterface;

        // Se non trovo la collection meta data non filtro
        if (!foundCollectionMetaData) {
            return propertyList;
        }

        return propertyList.filter((v) => {
            const pm = MetaDataUtils.getPropertyMetaDataFromPath(foundCollectionMetaData.dependentMetaData, v);
            if (pm && pm.userMetaData?.securityAccess === AccessMode.Deny) {
                return false;
            }

            // const splittedPropertyFullPath = v.split('.');
            // if (splittedPropertyFullPath.length === 1) {
            //     const pm = foundCollectionMetaData.dependentMetaData.getPropertyMetaData(v);
            //     if (pm && pm.userMetaData?.securityAccess === AccessMode.Deny) {
            //         return false;
            //     }
            // } else {

            // }
            return true;
        });
    }

    async initRootViewModel(
        domainModel: TModel,
        aggregateMetaData: AggregateMetaDataInterface,
        orchestratorViewModel: CoreOrchestratorViewModelInterface,
        ownerCollection?: CollectionViewModelInterface<any>,
        isNewItem?: boolean,
        path?: string,
        isMock = false,
        domainModelType: ClassConstructor<TModel> = null
    ) {

        this._columnsProvider = orchestratorViewModel;
        this.reservedPath = '';
        this.reservedName = '';
        this.isMock = isMock;

        await this.initAggregateElementViewModel({
            domainModel,
            aggregateMetaData,
            orchestratorViewModel,
            isMock,
            domainModelType
        });
    }

    /**
     * Viene utilizzata per inizializzare le custom grid column
     */
    async initCustomGridColumns(): Promise<void> {

    }

    setCustomColumnInfo(modelName: string, columnInfoCollection: ColumnInfoCollectionInterface) {
        this._columnsProvider.customGridColumns.set(modelName, columnInfoCollection);
    }
}
